import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Axe, Loader2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1580013364038-30865228668a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxydXN0aWMlMjBjYWJpbiUyMHN0dWR5JTIwZGVza3xlbnwwfHx8fDE3ODQwOTEyNjJ8MA&ixlib=rb-4.1.0&q=85";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = isLogin ? await login(email, password) : await register(name, email, password);
      toast.success(isLogin ? "Welcome back." : "Account created.");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-[calc(100vh-73px)] lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/70" />
        <div className="relative flex h-full flex-col justify-end p-14">
          <Axe className="h-10 w-10 text-primary" strokeWidth={2.5} />
          <h2 className="mt-5 font-head text-4xl font-black leading-tight">Academically Strong.<br />Prepared for Life.</h2>
          <p className="mt-3 max-w-sm text-muted-foreground">A complete K-12 homeschool curriculum built for families who value grit and grounding.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 text-primary lg:hidden">
            <Axe className="h-6 w-6" strokeWidth={2.5} /><span className="font-head text-xl font-bold text-foreground">HORNSBY</span>
          </div>
          <h1 className="mt-4 font-head text-4xl font-black lg:mt-0">{isLogin ? "Welcome Back" : "Create Account"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{isLogin ? "Log in to your members area." : "Join the Hornsby family in under a minute."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {!isLogin && (
              <div>
                <Label className="label-caps text-muted-foreground">Full Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="auth-name" className="mt-2 rounded-none border-border bg-card" />
              </div>
            )}
            <div>
              <Label className="label-caps text-muted-foreground">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="auth-email" className="mt-2 rounded-none border-border bg-card" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="auth-password" className="mt-2 rounded-none border-border bg-card" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-none label-caps bg-primary py-6 hover:bg-primary/80" data-testid="auth-submit">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : isLogin ? "Log In" : "Create Account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isLogin ? "New to Hornsby? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="font-semibold text-primary hover:underline" data-testid="auth-switch">
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
